import { CircularProgress, makeStyles, Typography } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import { useSnackbar } from 'notistack';
import { identity } from 'ramda';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Field, Form, FormRenderProps, useForm } from 'react-final-form';
import { useLocation } from 'react-router-dom';
import {
  Founder,
  FounderAdditionalInfoCreate,
  FounderDetails,
  FounderStatus,
} from '../../api/founders';
import { Specialization } from '../../api/specializations';
import { Role } from '../../api/user/Role';
import { UserContext } from '../../contexts/user-context';
import { useScrollOnValidation } from '../../hooks/useScrollOnValidation';
import { Name } from '../../types';
import {
  apply,
  Either,
  isLeft,
  left,
  parseObject,
  right,
} from '../../utils/Either';
import { Email, stringToEitherEmail } from '../../utils/String/Email';
import { Phone, stringToEitherPhone } from '../../utils/String/Phone';
import { fromString, Sentence } from '../../utils/String/Sentence';
import { isUrl } from '../../utils/String/Url';
import { stringToEitherZip, Zip } from '../../utils/String/Zip';
import { formatDateToRFC } from '../../utils/date';
import {
  PastStringDate,
  strToEitherPastStrDate,
} from '../../utils/date/PastStringDate';
import {
  GENDER_MENTOR,
  lengthField,
  RACE,
  RADIO_SELECT,
  STATUSES_FOUNDERS,
} from '../../utils/form';
import {
  checkSizeTablet,
  getAbbreviationByName,
  isMobile,
} from '../../utils/functions';
import { CLASS_TRACKING } from '../../utils/tracking_class';
import {
  ActivateConfirmationModal,
  BeforeUnload,
  Button,
  FieldNames,
  FormGroup,
  StickyContent,
  UserLogo,
} from '../common';
import { hasDuplicateEmail } from '../common/field-email';
import { checkNameForDublicates } from '../common/field-names';
import {
  FormDateInput,
  FormEmail,
  FormSelect,
  FormSpecializationInput,
  TextFieldWrapper,
} from './wrappers';

interface FounderDetailsFormProps {
  founderId?: Founder['id'];
  founderDetails?: FounderDetails;
  founderSpecializations?: Specialization['id'][];
  loading?: boolean;
  onSubmit: (parsedFormValues: ParsedFormValues) => any;
  isExternal?: boolean;
}

interface FormValues {
  address: string | Sentence<1024> | null;
  firstName: string | Name;
  lastName: string | Name;
  verifiedName: boolean;
  dateOfBirth: string | PastStringDate | null;
  dateOfEnrollment: string | PastStringDate | null;
  specializations: Specialization['id'][];
  about: Sentence<512> | string | null;
  currentRole: Sentence<255> | string | null;
  education: Sentence<512> | string | null;
  isSerialFounder: 'true' | 'false' | '';
  email: string | Email;
  verifiedEmail: boolean;
  phone: string | null | Phone;
  linkedInProfile: string | null | Sentence<250>;
  country: string | null | Sentence<250>;
  state: string | null | Sentence<250>;
  city: string | null | Sentence<250>;
  logo: string;
  zip: string | null | Zip;
  status: FounderStatus;
  gender: string | Sentence<20> | null;
  race: string | Sentence<50> | null;
}

interface ValidFormValues extends FormValues {
  address: Sentence<1024> | null;
  firstName: Name;
  lastName: Name;
  verifiedName: boolean;
  dateOfBirth: PastStringDate | null;
  dateOfEnrollment: PastStringDate | null;
  specializations: Specialization['id'][];
  about: Sentence<512>;
  currentRole: Sentence<255> | null;
  education: Sentence<512> | null;
  isSerialFounder: 'true' | 'false';
  email: Email;
  verifiedEmail: boolean;
  phone: null | Phone;
  linkedInProfile: null | Sentence<250>;
  country: null | Sentence<250>;
  state: null | Sentence<250>;
  city: null | Sentence<250>;
  logo: string;
  zip: null | Zip;
  gender: Sentence<20> | null;
  race: Sentence<50> | null;
}

interface FounderDetailsValues
  extends Omit<FounderDetails, 'foundersAdditionalInfo' | 'dateOfEnrollment'> {
  dateOfEnrollment: PastStringDate | null;
  foundersAdditionalInfo: Omit<
    FounderAdditionalInfoCreate,
    'veteran' | 'creationDate'
  >;
}

export interface ParsedFormValues {
  values: FounderDetailsValues;
  specializations: Specialization['id'][];
  logoFile: File | null;
}

const isEnrollmentDateRequired = (status: FounderStatus) =>
  status === 'Active';

type Errors = {
  [K in keyof FormValues]?: string;
};

const useStyles = makeStyles((theme) => ({
  sectionBlock: {
    '& + &': {
      marginTop: 56,
    },
  },
  sectionTitle: {
    marginBottom: 32,
  },
  formBlocks: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  logoFormBlock: {
    margin: '0 0 40px 0',

    [theme.breakpoints.up('xs')]: {
      margin: '65px 0 0 30px',
    },

    [theme.breakpoints.up(1000)]: {
      marginLeft: 80,
    },
  },
  mainFormBlock: {
    width: '100%',

    [theme.breakpoints.up('xs')]: {
      width: 560,
    },
  },
  actionsBlock: {
    marginTop: 56,
  },
  statusField: {
    marginTop: 0,

    [theme.breakpoints.up('xs')]: {
      marginTop: 15,
    },
  },
  educationField: {
    flex: '7 1 0 !important',
    minWidth: 0,
    '& > *': {
      width: '100%',
    },
  },
  serialFounderField: {
    flex: '3 1 0 !important',
    minWidth: 0,
    '& > *': {
      width: '100%',
    },
  },
  aboutField: {
    minHeight: 64,
  },
}));

function getValidFormValue(
  formValues: FormValues,
): Either<Errors, ValidFormValues> {
  return parseObject<FormValues, ValidFormValues, Errors>(
    {
      address: (v) => (v ? fromString(1024, v) : right(null)),
      city: (v) => (v ? fromString(250, v) : right(null)),
      country: (v) => (v ? fromString(250, v) : right(null)),
      dateOfBirth: (v) => (v ? strToEitherPastStrDate(v) : right(null)),
      lastName: (v) => fromString(256, v || ''),
      firstName: (v) => fromString(256, v || ''),
      email: (v) => stringToEitherEmail(v || ''),
      logo: (v) => right(v),
      phone: (v) => (v ? stringToEitherPhone(v) : right(null)),
      dateOfEnrollment: (v) =>
        !v
          ? isEnrollmentDateRequired(formValues.status)
            ? left('Required')
            : right(null)
          : strToEitherPastStrDate(v),
      specializations: (v) => right(v),
      about: (v) => fromString(512, v ?? ''),
      currentRole: (v) => (v ? fromString(255, v) : right(null)),
      education: (v) => (v ? fromString(512, v) : right(null)),
      isSerialFounder: (v) => right(v || 'false'),
      state: (v) => (v ? fromString(250, v) : right(null)),
      linkedInProfile: (v) =>
        v ? (isUrl(v) ? fromString(250, v) : left('Invalid URL')) : right(null),
      status: (v) => right(v),
      zip: (v) => (v ? stringToEitherZip(v) : right(null)),
      verifiedEmail: (v) => right(v),
      verifiedName: (v) => right(v),
      gender: (v) => (v ? fromString(20, v) : right(null)),
      race: (v) => (v ? fromString(50, v) : right(null)),
    },
    formValues,
  );
}

const validateForm = (values: FormValues) => {
  const errors = getValidFormValue(values);

  return isLeft(errors) ? errors.value : {};
};

function getInitialValues(
  founderDetails?: FounderDetails,
  specializations: Specialization['id'][] = [],
): FormValues {
  const country = founderDetails?.country || '';

  return {
    address: founderDetails?.address || '',
    firstName: founderDetails?.firstName || '',
    lastName: founderDetails?.lastName || '',
    verifiedName: !!(founderDetails?.firstName && founderDetails.lastName),
    dateOfBirth: founderDetails?.dateOfBirth || '',
    dateOfEnrollment: founderDetails?.dateOfEnrollment || '',
    specializations,
    about: founderDetails?.about || '',
    currentRole: founderDetails?.currentRole || '',
    education: founderDetails?.education || '',
    isSerialFounder: founderDetails
      ? founderDetails.isSerialFounder
        ? 'true'
        : 'false'
      : '',
    email: founderDetails?.email || '',
    verifiedEmail: !!founderDetails?.email,
    phone: founderDetails?.phone || '',
    linkedInProfile: founderDetails?.linkedInProfile || '',
    country,
    state: founderDetails?.state || '',
    city: founderDetails?.city || '',
    logo: founderDetails?.logo || '',
    zip: founderDetails?.zip || '',
    status: founderDetails?.status || STATUSES_FOUNDERS[2].value,
    gender: founderDetails?.foundersAdditionalInfo?.gender || '',
    race: founderDetails?.foundersAdditionalInfo?.race || '',
  };
}

function getParsedValues(
  formValues: ValidFormValues,
  logoFile: File | null,
): ParsedFormValues {
  return {
    values: {
      address: formValues.address,
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      about: formValues.about,
      currentRole: formValues.currentRole,
      education: formValues.education,
      isSerialFounder: formValues.isSerialFounder === 'true',
      dateOfBirth: formValues.dateOfBirth,
      dateOfEnrollment: formValues.dateOfEnrollment || null,
      email: formValues.email,
      phone: formValues.phone,
      linkedInProfile: formValues.linkedInProfile,
      country: formValues.country,
      state: formValues.state,
      city: formValues.city,
      logo: formValues.logo,
      zip: formValues.zip,
      status: formValues?.status,
      foundersAdditionalInfo: {
        gender: formValues.gender,
        race: formValues.race,
      },
    },
    specializations: formValues.specializations,
    logoFile,
  };
}

function FounderDetailsForm({
  founderDetails,
  founderSpecializations,
  loading = false,
  onSubmit,
  isExternal = false,
}: FounderDetailsFormProps) {
  const classes = useStyles();
  const location = useLocation();
  const setSubmitValidationFailed = useScrollOnValidation();
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [isAsyncValidating, setIsAsyncValidating] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [pendingStatusChange, setPendingStatusChange] =
    useState<FounderStatus | null>(null);
  const { hasAccessToAction: checkAccess, hasRole } = useContext(UserContext);
  const hasAccessToAction = useMemo(
    () => (isExternal ? () => true : checkAccess),
    [checkAccess, isExternal],
  );

  const hasAccessToUpdateAdditionalInfo = useMemo(() => {
    const isExternalPage = location.pathname.includes('external');
    return isExternalPage
      ? true
      : hasAccessToAction('founder.additionalInfo.update');
  }, [hasAccessToAction, location.pathname]);

  const handleSubmit = async (formValues: FormValues) => {
    if (isAsyncValidating) {
      return;
    }

    try {
      setIsAsyncValidating(true);
      const validValues = getValidFormValue(formValues);

      if (isLeft(validValues)) {
        throw new Error('Invalid form values');
      }

      const parsedValues = getParsedValues(validValues.value, logoFile);

      const isEmailDublicate =
        parsedValues.values.email !== founderDetails?.email &&
        (await hasDuplicateEmail('founder', parsedValues.values.email));

      if (isEmailDublicate) {
        throw new Error('Email is dublicate');
      }

      const nameCheckStatus =
        parsedValues.values.firstName === founderDetails?.firstName &&
          parsedValues.values.lastName === founderDetails?.lastName
          ? 'success'
          : await checkNameForDublicates(
            'founder',
            parsedValues.values.firstName,
            parsedValues.values.lastName,
          );

      if (nameCheckStatus !== 'success') {
        throw new Error('Name is dublicate');
      }

      setIsAsyncValidating(false);
    } catch (error) {
      setIsAsyncValidating(false);
      return;
    }

    return apply(
      identity,
      (validValues) => onSubmit(getParsedValues(validValues, logoFile)),
      getValidFormValue(formValues),
    );
  };

  const initialValues = useMemo(
    () => getInitialValues(founderDetails, founderSpecializations),
    [founderDetails, founderSpecializations],
  );

  const formAPIRef = useRef<any>(null);

  const handleStatusChange = (
    newStatus: FounderStatus,
    currentStatus: FounderStatus,
  ) => {
    if (initialValues.status === 'Applicant' && newStatus === 'Active') {
      setPendingStatusChange('Active');
      return currentStatus;
    }
    if (
      newStatus === 'Active' &&
      formAPIRef.current &&
      !formAPIRef.current.getState().values.dateOfEnrollment
    ) {
      formAPIRef.current.change('dateOfEnrollment', formatDateToRFC(new Date()));
    }
    return newStatus;
  };

  const handleActivateConfirm = () => {
    if (pendingStatusChange && formAPIRef.current) {
      formAPIRef.current.change('status', pendingStatusChange);
      if (!formAPIRef.current.getState().values.dateOfEnrollment) {
        formAPIRef.current.change('dateOfEnrollment', formatDateToRFC(new Date()));
      }
    }
    setPendingStatusChange(null);
  };

  const handleActivateCancel = () => {
    setPendingStatusChange(null);
  };

  const validateLogoFile = (logo: File) => {
    const fileName = logo.name;
    if (fileName.length > lengthField.logoFileName) {
      enqueueSnackbar('The maximum length of the file name is 75 characters', {
        variant: 'error',
      });
      return false;
    }
    return true;
  };

  return (
    <div>
      <Form
        validate={validateForm}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={(formProps) => {
          setSubmitValidationFailed(
            formProps.submitFailed &&
            !formProps.dirtySinceLastSubmit &&
            !formProps.submitting,
          );
          return (
            <FormWrapper formProps={formProps} formAPIRef={formAPIRef}>
              <BeforeUnload
                when={formProps.dirty && !loading}
                title='Leave the page'
                body='You are about to leave the page, all unsaved changes will be lost. Do you want to continue?'
                disabled={loading}
                confirmButtonRenderer={({ onConfirm }) => (
                  <Button
                    variant='outlined'
                    onClick={async () => {
                      await formProps.handleSubmit();
                      onConfirm();
                    }}
                    disabled={loading || !formProps.valid}
                  >
                    {loading ? (
                      <CircularProgress size={24} color='inherit' />
                    ) : (
                      'Save the changes'
                    )}
                  </Button>
                )}
              />
              <div className={classes.formBlocks}>
                <div className={classes.mainFormBlock}>
                  <form noValidate>
                    <div className={classes.sectionBlock}>
                      {isMobile() && (
                        <div className={classes.logoFormBlock}>
                          <Field<string>
                            name='logo'
                            parse={(value) => (!value ? '' : value)}
                            component={({ input }) => (
                              <UserLogo
                                logo={input.value}
                                name={getAbbreviationByName(
                                  formProps.values.firstName,
                                  formProps.values.lastName,
                                )}
                                onUpload={(photoFile) => {
                                  if (validateLogoFile(photoFile)) {
                                    setLogoFile(photoFile);
                                    const blobFile =
                                      URL.createObjectURL(photoFile);
                                    input.onChange(blobFile);
                                  }
                                }}
                                onRemove={() => {
                                  setLogoFile(null);
                                  input.onChange('');
                                }}
                                readOnly={
                                  !hasAccessToAction('founder.details.update')
                                }
                              />
                            )}
                          />
                        </div>
                      )}
                      {hasAccessToAction('founder.details.field.firstName') &&
                        hasAccessToAction('founder.details.field.lastName') && (
                          <FieldNames
                            entityType='founder'
                            isReadOnly={
                              !hasAccessToAction('founder.details.update')
                            }
                          />
                        )}
                      {hasAccessToAction('founder.details.field.dateOfBirth') &&
                        hasAccessToAction(
                          'founder.details.field.dateOfEnrollment',
                        ) && (
                          <FormGroup mobile={checkSizeTablet(800)}>
                            <Field<string>
                              testid='founder-details-date-birth'
                              name='dateOfBirth'
                              component={FormDateInput}
                              label='Date of birth'
                              editable
                              parse={(value) => {
                                try {
                                  if (value) {
                                    return formatDateToRFC(value);
                                  }
                                  return '';
                                } catch (e: any) {
                                  return 'invalid';
                                }
                              }}
                            />
                            <Field<string>
                              testid='founder-details-date-enrollment'
                              name='dateOfEnrollment'
                              component={FormDateInput}
                              label={`Date of enrollment${isEnrollmentDateRequired(formProps.values.status)
                                  ? '*'
                                  : ''
                                }`}
                              maxDate={new Date()}
                              editable
                              parse={(value) => {
                                try {
                                  if (value) {
                                    return formatDateToRFC(value);
                                  }
                                  return '';
                                } catch (e: any) {
                                  return 'invalid';
                                }
                              }}
                            />
                          </FormGroup>
                        )}
                      <FormGroup>
                        <Field<string>
                          name='gender'
                          testid='founder-details-gender'
                          component={FormSelect}
                          label='Gender'
                          options={GENDER_MENTOR}
                          readOnly={!hasAccessToUpdateAdditionalInfo}
                        />
                        <Field<string>
                          name='race'
                          testid='founder-details-race'
                          component={FormSelect}
                          label='Race'
                          options={RACE}
                          readOnly={!hasAccessToUpdateAdditionalInfo}
                        />
                      </FormGroup>
                      {hasAccessToAction(
                        'founder.details.field.specializations',
                      ) && (
                          <FormGroup>
                            <Field<Specialization['id'][]>
                              name='specializations'
                              data-testid='founder-details-specializations'
                              component={FormSpecializationInput}
                              label='Specialization'
                              disabled={!founderSpecializations}
                            />
                          </FormGroup>
                        )}
                      {isMobile() && (
                        <FormGroup className={classes.statusField}>
                          <Field<string>
                            testid='founder-details-status'
                            name='status'
                            component={FormSelect}
                            label='Status'
                            options={STATUSES_FOUNDERS}
                            readOnly={
                              !(hasRole(Role.Admin) || hasRole(Role.Manager)) ||
                              pendingStatusChange !== null
                            }
                            parse={(value) =>
                              handleStatusChange(
                                value as FounderStatus,
                                formProps.values.status,
                              )
                            }
                          />
                        </FormGroup>
                      )}
                      <FormGroup id='about'>
                        <Field<string>
                          testid='founder-details-about'
                          name='about'
                          component={TextFieldWrapper}
                          parse={(value) => (!value ? '' : value)}
                          label='About*'
                          multiline
                          InputProps={{
                            inputProps: {
                              className: classes.aboutField,
                              maxLength: 512,
                              readOnly: !hasAccessToUpdateAdditionalInfo,
                            },
                          }}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Field<string>
                          name='currentRole'
                          data-testid='founder-details-current-role'
                          component={TextFieldWrapper}
                          label='Current Role'
                          parse={(value) => (!value ? '' : value)}
                          InputProps={{
                            inputProps: {
                              maxLength: lengthField.currentRole,
                              readOnly: !hasAccessToAction(
                                'founder.details.update',
                              ),
                            },
                          }}
                        />
                      </FormGroup>
                      <FormGroup mobile={checkSizeTablet(800)}>
                        <div className={classes.educationField}>
                          <Field<string>
                            name='education'
                            data-testid='founder-details-education'
                            component={TextFieldWrapper}
                            label='Education'
                            parse={(value) => (!value ? '' : value)}
                            InputProps={{
                              inputProps: {
                                maxLength: lengthField.education,
                                readOnly: !hasAccessToAction(
                                  'founder.details.update',
                                ),
                              },
                            }}
                          />
                        </div>
                        <div className={classes.serialFounderField}>
                          <Field<string>
                            name='isSerialFounder'
                            testid='founder-details-is-serial-founder'
                            component={FormSelect}
                            label='Serial Founder'
                            options={RADIO_SELECT}
                            readOnly={
                              !hasAccessToAction('founder.details.update')
                            }
                          />
                        </div>
                      </FormGroup>
                    </div>
                    <div className={classes.sectionBlock}>
                      <Typography className={classes.sectionTitle} variant='h3'>
                        Contacts
                      </Typography>
                      {hasAccessToAction('founder.details.field.email') && (
                        <FormGroup>
                          <Field<string>
                            data-testid='founder-details-email'
                            name='email'
                            component={FormEmail}
                            label='Email*'
                            formatOnBlur
                            format={(value: string) => {
                              return value ? value.toLowerCase() : value;
                            }}
                            entityType='founder'
                            InputProps={{
                              inputProps: {
                                maxLength: lengthField.email,
                                readOnly: !hasAccessToAction(
                                  'founder.details.update',
                                ),
                              },
                            }}
                            type='founder'
                          />
                        </FormGroup>
                      )}
                      {hasAccessToAction('founder.details.field.phone') && (
                        <FormGroup>
                          <Field<string>
                            data-testid='founder-details-phone'
                            name='phone'
                            label='Phone'
                            component={TextFieldWrapper}
                            InputProps={{
                              inputProps: {
                                maxLength: lengthField.phone,
                                readOnly: !hasAccessToAction(
                                  'founder.details.update',
                                ),
                              },
                            }}
                          />
                        </FormGroup>
                      )}
                      {hasAccessToAction(
                        'founder.details.field.linkedInProfile',
                      ) && (
                          <FormGroup>
                            <Field<string>
                              data-testid='founder-details-linkedin'
                              name='linkedInProfile'
                              component={TextFieldWrapper}
                              label='LinkedIn profile'
                              placeholder='https://www.linkedin.com/in/your-profile'
                              InputProps={{
                                inputProps: {
                                  maxLength: lengthField.linkedIn,
                                  readOnly: !hasAccessToAction(
                                    'founder.details.update',
                                  ),
                                },
                              }}
                            />
                          </FormGroup>
                        )}
                    </div>
                    {hasAccessToAction('founder.details.field.address') && (
                      <div className={classes.sectionBlock}>
                        <Typography
                          className={classes.sectionTitle}
                          variant='h3'
                        >
                          Address
                        </Typography>
                        <FormGroup>
                          <Field<string>
                            data-testid='founder-details-address'
                            name='address'
                            component={TextFieldWrapper}
                            label='Address'
                            InputProps={{
                              inputProps: {
                                maxLength: lengthField.address,
                              },
                            }}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Field<string>
                            data-testid='founder-details-city'
                            name='city'
                            component={TextFieldWrapper}
                            label='City'
                            InputProps={{
                              inputProps: {
                                maxLength: lengthField.city,
                              },
                            }}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Field<string>
                            data-testid='founder-details-state'
                            name='state'
                            component={TextFieldWrapper}
                            label='State'
                            InputProps={{
                              inputProps: {
                                maxLength: lengthField.state,
                              },
                            }}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Field<string>
                            name='zip'
                            data-testid='founder-details-zip'
                            component={TextFieldWrapper}
                            label='Zip code'
                            InputProps={{
                              inputProps: {
                                maxLength: lengthField.zip,
                              },
                            }}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Field<string>
                            data-testid='founder-details-country'
                            name='country'
                            component={TextFieldWrapper}
                            label='Country'
                            InputProps={{
                              inputProps: {
                                maxLength: lengthField.country,
                              },
                            }}
                          />
                        </FormGroup>
                      </div>
                    )}
                    {hasAccessToAction('founder.details.update') && (
                      <div className={classes.actionsBlock}>
                        <StickyContent>
                          <Button
                            data-testid='founder-details-button-submit'
                            className={CLASS_TRACKING.INTERNAL_ACTION}
                            onClick={formProps.handleSubmit}
                            disabled={loading}
                            startIcon={<CheckIcon />}
                          >
                            {loading ? (
                              <CircularProgress size={24} color='inherit' />
                            ) : (
                              'Save'
                            )}
                          </Button>
                        </StickyContent>
                      </div>
                    )}
                  </form>
                </div>
                {!isMobile() && (
                  <div className={classes.logoFormBlock}>
                    <Field<string>
                      name='logo'
                      parse={(value) => (!value ? '' : value)}
                      component={({ input }) => (
                        <UserLogo
                          logo={input.value}
                          name={getAbbreviationByName(
                            formProps.values.firstName,
                            formProps.values.lastName,
                          )}
                          onUpload={(photoFile) => {
                            if (validateLogoFile(photoFile)) {
                              setLogoFile(photoFile);
                              const blobFile = URL.createObjectURL(photoFile);
                              input.onChange(blobFile);
                            }
                          }}
                          onRemove={() => {
                            setLogoFile(null);
                            input.onChange('');
                          }}
                          readOnly={
                            !hasAccessToAction('founder.details.update')
                          }
                        />
                      )}
                    />

                    <FormGroup className={classes.statusField}>
                      <Field<string>
                        testid='founder-details-status'
                        name='status'
                        component={FormSelect}
                        label='Status'
                        options={STATUSES_FOUNDERS}
                        readOnly={
                          !(hasRole(Role.Admin) || hasRole(Role.Manager))
                        }
                        parse={(value) =>
                          handleStatusChange(
                            value as FounderStatus,
                            formProps.values.status,
                          )
                        }
                      />
                    </FormGroup>
                  </div>
                )}
              </div>
            </FormWrapper>
          );
        }}
      />
      <ActivateConfirmationModal
        open={pendingStatusChange !== null}
        onClose={handleActivateCancel}
        onConfirm={handleActivateConfirm}
      />
    </div>
  );
}

function FormWrapper({
  formProps,
  children,
  formAPIRef,
}: {
  formProps: FormRenderProps<FormValues, FormValues>;
  children: any;
  formAPIRef: React.MutableRefObject<any>;
}) {
  const formAPI = useForm();

  useEffect(() => {
    formAPIRef.current = formAPI;
    formAPI.change('logo', formProps.initialValues.logo);
  }, [formAPI, formProps.initialValues.logo, formAPIRef]);

  return children;
}

export default FounderDetailsForm;
